import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Globe, ArrowRight, ArrowLeft, Mail, Key, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const { language, toggleLanguage, isRTL } = useLanguage();
  const [step, setStep] = useState(1); // 1: email, 2: token, 3: success
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'ar' ? 'حدث خطأ' : 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError(language === 'ar' ? 'كلمات المرور غير متطابقة' : 'Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      setError(language === 'ar' ? 'كلمة المرور يجب أن تكون 8 أحرف على الأقل' : 'Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'ar' ? 'حدث خطأ' : 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  const BackArrow = isRTL ? ArrowRight : ArrowLeft;

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_877cf3ae-79e1-4b85-af95-e3f38fcbc2fb/artifacts/7faxr3je_img-1.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary/80 to-primary/40 backdrop-blur-sm" />

      <Button
        variant="ghost"
        size="icon"
        onClick={toggleLanguage}
        className="absolute top-4 end-4 z-10 bg-white/20 hover:bg-white/30 text-white"
        data-testid="forgot-language-toggle"
      >
        <Globe size={20} />
      </Button>

      <Card className="w-full max-w-md relative z-10 shadow-2xl border-0 bg-card/95 backdrop-blur-xl">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 w-20 h-20 rounded-2xl flex items-center justify-center overflow-hidden">
            {step === 3 ? (
              <CheckCircle className="h-12 w-12 text-green-500" />
            ) : (
              <img 
                src="https://customer-assets.emergentagent.com/job_877cf3ae-79e1-4b85-af95-e3f38fcbc2fb/artifacts/imx8202j_909ff97a-29cc-4bc8-9a25-59a8865473f7-300x294.png" 
                alt="Qassion Bakeries Logo" 
                className="w-full h-full object-contain"
              />
            )}
          </div>
          <CardTitle className="font-heading text-2xl text-primary">
            {step === 3 
              ? (language === 'ar' ? 'تم بنجاح!' : 'Success!')
              : (language === 'ar' ? 'نسيت كلمة المرور' : 'Forgot Password')
            }
          </CardTitle>
          <CardDescription className="text-base">
            {step === 1 && (language === 'ar' 
              ? 'أدخل بريدك الإلكتروني لاستلام رمز إعادة التعيين'
              : 'Enter your email to receive a reset code'
            )}
            {step === 2 && (language === 'ar'
              ? 'أدخل الرمز المرسل إلى بريدك وكلمة المرور الجديدة'
              : 'Enter the code sent to your email and new password'
            )}
            {step === 3 && (language === 'ar'
              ? 'تم إعادة تعيين كلمة المرور بنجاح'
              : 'Your password has been reset successfully'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm text-center" data-testid="forgot-error">
              {error}
            </div>
          )}

          {step === 1 && (
            <form onSubmit={handleSendEmail} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  required
                  data-testid="forgot-email-input"
                  className="h-12"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold rounded-xl"
                disabled={loading}
                data-testid="forgot-submit-btn"
              >
                {loading 
                  ? (language === 'ar' ? 'جاري الإرسال...' : 'Sending...')
                  : (language === 'ar' ? 'إرسال رمز التحقق' : 'Send Reset Code')
                }
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="token">{language === 'ar' ? 'رمز التحقق' : 'Reset Code'}</Label>
                <Input
                  id="token"
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder={language === 'ar' ? 'الرمز المرسل إلى بريدك' : 'Code sent to your email'}
                  required
                  data-testid="reset-token-input"
                  className="h-12 font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">{language === 'ar' ? 'كلمة المرور الجديدة' : 'New Password'}</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={8}
                  data-testid="reset-new-password-input"
                  className="h-12"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{language === 'ar' ? 'تأكيد كلمة المرور' : 'Confirm Password'}</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={8}
                  data-testid="reset-confirm-password-input"
                  className="h-12"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold rounded-xl"
                disabled={loading}
                data-testid="reset-submit-btn"
              >
                {loading 
                  ? (language === 'ar' ? 'جاري التحديث...' : 'Updating...')
                  : (language === 'ar' ? 'تحديث كلمة المرور' : 'Update Password')
                }
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => setStep(1)}
                data-testid="reset-back-btn"
              >
                {language === 'ar' ? 'إعادة إرسال الرمز' : 'Resend Code'}
              </Button>
            </form>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <p className="text-green-700">
                  {language === 'ar' 
                    ? 'يمكنك الآن تسجيل الدخول بكلمة المرور الجديدة'
                    : 'You can now login with your new password'
                  }
                </p>
              </div>
              <Link to="/login">
                <Button
                  className="w-full h-12 text-base font-semibold rounded-xl"
                  data-testid="goto-login-btn"
                >
                  {language === 'ar' ? 'الذهاب لتسجيل الدخول' : 'Go to Login'}
                </Button>
              </Link>
            </div>
          )}

          {step !== 3 && (
            <div className="mt-6 pt-6 border-t border-border/50 text-center">
              <Link 
                to="/login" 
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                data-testid="back-to-login"
              >
                <BackArrow size={16} />
                {language === 'ar' ? 'العودة لتسجيل الدخول' : 'Back to Login'}
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
